<script setup>
// Paid-RSVP flow (mockups meet-rsvp-summary / -summary-fees / -terms / -card /
// -confirm / -waitlist / -done). FREE meets never open this dialog — the page
// does a one-tap POST rsvp instead.
//
// The steps collapse based on what the member still needs:
//   summary  — price & policy (face price; card fees are never surcharged)
//   terms    — meet.termsHtml, versioned acceptance (shown while
//              meet.myTermsAccepted === false; covers both first-time and
//              terms-changed — the API exposes one flag for both)
//   confirm  — card on file + final confirm ("Save card & attend" when a card
//              still needs adding — AddCardDialog opens stacked)
//   done     — restates the money facts
//
// POST meets/{id}/acceptTerms runs just before POST meetOccurrences/{id}/rsvp
// so closing the dialog half-way never leaves a dangling acceptance.
import {computed, ref, watch} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import AddCardDialog from '~/components/payments/AddCardDialog.vue';
import MeetCancellationTermsDialog from '~/components/meets/MeetCancellationTermsDialog.vue';
import {hasCancellationTerms, bookingCancellationWarning} from '~/utils/meetCancellationTerms';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {usePaymentsDisplay} from '~/composables/usePaymentsDisplay';
import {
  bookLabel,
  CHARGED_AFTER_LINE,
  NO_SHOW_LINE,
  WAITLIST_CHARGE_LINE,
  payeeLine,
} from '~/composables/usePayingHonestly.js';
import {useMeetFormat} from '~/composables/useMeetFormat';
import {endpoints} from '~/network/endpoints.js';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  meet: {type: Object, required: true},
  occurrence: {type: Object, required: true},
  // Full register → the flow becomes "join the waitlist"
  waitlist: {type: Boolean, default: false},
});
const emit = defineEmits(['update:modelValue', 'updated']);

const {httpRequest} = useHttpRequest();
const {money, cardLabel, isCardExpired} = usePaymentsDisplay();
const {
  occurrenceWhen, chargeTimingText,
  freeCancellationUntil, dateFeeMinor, dateFeeText,
  termsAsHtml,
} = useMeetFormat();

// ── saved cards (loaded when the dialog opens) ──
const cards = ref(null); // null = loading
const loadCards = async () => {
  try {
    cards.value = await httpRequest(endpoints.payments.myPaymentMethods, null);
  } catch {
    cards.value = [];
  }
};
const usableCards = computed(() => (cards.value ?? []).filter(c => !isCardExpired(c)));
const defaultCard = computed(() =>
    usableCards.value.find(c => c.isDefault) ?? usableCards.value[0] ?? null);
const hasCard = computed(() => !!defaultCard.value);

// ── flow ──
const needsTerms = computed(() => !!props.meet.termsHtml && props.meet.myTermsAccepted === false);
const steps = computed(() => {
  const flow = [];
  if (needsTerms.value) flow.push('summary', 'terms');
  flow.push('confirm');
  return flow;
});
const stepIndex = ref(0);
const step = computed(() => submitted.value ? 'done' : steps.value[stepIndex.value]);
const stepLabel = computed(() =>
    steps.value.length > 1 ? `Step ${Math.min(stepIndex.value + 1, steps.value.length)} of ${steps.value.length}` : null);

const termsAccepted = ref(false);
const submitted = ref(false);
const pending = ref(false);
const errors = ref({});
const addCardOpen = ref(false);
const cancellationTermsOpen = ref(false);

watch(() => props.modelValue, (open) => {
  if (open) {
    stepIndex.value = 0;
    termsAccepted.value = false;
    submitted.value = false;
    errors.value = {};
    if (cards.value == null) loadCards();
  }
});

const close = () => emit('update:modelValue', false);
const back = () => { if (stepIndex.value > 0) stepIndex.value--; };
const next = () => { if (stepIndex.value < steps.value.length - 1) stepIndex.value++; };

// The price THIS date bills at (see dateFeeText); it is also the price on the button.
const dateFee = computed(() => dateFeeText(props.meet, props.occurrence));

const waitlistPosition = computed(() => (props.occurrence.waitlistedCount ?? 0) + 1);
const freeUntil = computed(() => freeCancellationUntil(props.meet, props.occurrence));
// Booking inside a cancellation window: what cancelling would cost from now,
// and that a refilled spot is free. The waitlist is told at promotion instead.
const bookingWarning = computed(() => props.waitlist ? null : bookingCancellationWarning({
  tiers: props.meet?.cancellationTiers,
  feeMinor: dateFeeMinor(props.meet, props.occurrence),
  currency: props.meet?.currency,
  startsAt: props.occurrence?.startsAt,
}));

// "£6.50 will be charged to Visa 4242 after the event." or
// "£6.50 will be charged to Visa 4242, added to your weekly bill."
const doneChargeLine = computed(() => {
  const timing = chargeTimingText(props.meet);
  const fee = dateFee.value + (hasCard.value ? ` will be charged to ${cardLabel(defaultCard.value)}` : '');
  return timing === 'charged after the event' ? `${fee} after the event.` : `${fee}, ${timing}.`;
});

// CCRs reg 13: the club is the seller, named with its address.
const payee = computed(() => payeeLine(props.meet.clubName, props.meet.clubAddress));

const title = computed(() => {
  if (step.value === 'terms') return 'Payment terms';
  if (props.waitlist) return 'Join the waitlist';
  return 'Attend this event';
});

const confirm = async () => {
  pending.value = true;
  errors.value = {};
  try {
    if (needsTerms.value) {
      await httpRequest(endpoints.meets.acceptTerms(props.meet.id), {});
    }
    const res = await httpRequest(endpoints.meetOccurrences.rsvp(props.occurrence.id), {attending: true});
    submitted.value = true;
    emit('updated', res);
  } catch (e) {
    errors.value = (e && typeof e === 'object') ? e : {global: ['Could not RSVP. Please try again.']};
  } finally {
    pending.value = false;
  }
};

const onCardSaved = async () => {
  addCardOpen.value = false;
  cards.value = null;
  await loadCards();
};
</script>

<template>
  <tl-dialog :model-value="modelValue" :max-width="480" @update:model-value="emit('update:modelValue', $event)">
    <template #title>
      {{ title }}
      <span v-if="stepLabel && step !== 'done'" class="rsvp-step">{{ stepLabel }}</span>
    </template>

    <!-- ── STEP: price & policy summary (first-time flows) ── -->
    <template v-if="step === 'summary'">
      <div class="sum-row"><span class="k">Event</span><span class="v">{{ meet.name }} · {{ occurrenceWhen(occurrence) }}</span></div>
      <div class="sum-row"><span class="k">Fee</span><span class="v">{{ dateFee }}</span></div>
      <div class="sum-row"><span class="k">Charged</span><span class="v">{{ chargeTimingText(meet) === 'charged after the event' ? 'After the event · nothing taken now' : `Nothing taken now, ${chargeTimingText(meet)}` }}</span></div>
      <div v-if="waitlist" class="sum-row"><span class="k">Your position</span><span class="v">#{{ waitlistPosition }}</span></div>
      <div v-if="freeUntil" class="sum-row"><span class="k">Free cancellation</span><span class="v">Until {{ freeUntil }}</span></div>
      <div class="assure">
        <v-icon size="17" icon="mdi-shield-check-outline"/>
        <span v-if="waitlist">{{ WAITLIST_CHARGE_LINE }} If a spot opens you get first refusal. Declining a promotion is always free.</span>
        <span v-else>Your card is saved securely with Stripe. {{ CHARGED_AFTER_LINE }}<template v-if="freeUntil"> Cancel any time before {{ freeUntil }} at no cost.</template></span>
      </div>
      <div v-if="bookingWarning" class="assure assure--warn">
        <v-icon size="17" icon="mdi-alert-outline"/>
        <span>{{ bookingWarning }}</span>
      </div>
    </template>

    <!-- ── STEP: terms (versioned acceptance) ── -->
    <template v-else-if="step === 'terms'">
      <div class="terms-intro">
        {{ meet.clubName }} asks you to accept its payment &amp; cancellation terms before attending paid
        events. You'll only see this once (unless the terms change).
      </div>
      <div class="terms-box" v-html="termsAsHtml(meet.termsHtml)"/>
      <v-checkbox v-model="termsAccepted" density="compact" hide-details class="mt-2">
        <template #label>
          <span class="terms-accept">I accept {{ meet.clubName }}'s payment &amp; cancellation terms</span>
        </template>
      </v-checkbox>
    </template>

    <!-- ── STEP: confirm (card row; "save a card" hand-off when none) ── -->
    <template v-else-if="step === 'confirm'">
      <p v-if="payee && !waitlist" class="payee">{{ payee }}</p>
      <div class="sum-row"><span class="k">Event</span><span class="v">{{ meet.name }} · {{ occurrenceWhen(occurrence) }}</span></div>
      <div class="sum-row">
        <span class="k">Fee</span>
        <span class="v">{{ dateFee }} · {{ waitlist ? 'nothing unless you accept a spot' : chargeTimingText(meet) }}</span>
      </div>
      <div v-if="waitlist" class="sum-row"><span class="k">Your position</span><span class="v">#{{ waitlistPosition }}</span></div>
      <div class="sum-row">
        <span class="k">Card</span>
        <span v-if="cards == null" class="v"><v-progress-circular indeterminate size="14" width="2"/></span>
        <span v-else-if="hasCard" class="v v--card">
          <v-icon size="16" icon="mdi-credit-card-outline"/>
          {{ cardLabel(defaultCard) }}
          <router-link :to="{path: '/your-profile', query: {tab: 'payments'}}" class="v__link">Change</router-link>
        </span>
        <span v-else class="v v--card">
          <v-icon size="16" icon="mdi-credit-card-off-outline" color="warning"/>
          No saved card
          <a href="#" class="v__link" @click.prevent="addCardOpen = true">Add a card</a>
        </span>
      </div>
      <div v-if="cards != null && !hasCard" class="assure assure--warn">
        <v-icon size="17" icon="mdi-credit-card-plus-outline"/>
        <span>
          A saved card is needed to attend paid dates. Nothing is taken now.
          <a href="#" @click.prevent="addCardOpen = true">Add one here</a> or in
          <router-link :to="{path: '/your-profile', query: {tab: 'payments'}}">your payments page</router-link>.
        </span>
      </div>
      <div v-else class="assure">
        <v-icon size="17" icon="mdi-shield-check-outline"/>
        <span v-if="waitlist">If a spot opens you get first refusal with a confirm window. Declining a promotion is always free.</span>
        <span v-else>Nothing taken now<template v-if="freeUntil">. Free cancellation until {{ freeUntil }}</template>.</span>
      </div>
      <div v-if="bookingWarning" class="assure assure--warn">
        <v-icon size="17" icon="mdi-alert-outline"/>
        <span>{{ bookingWarning }}</span>
      </div>
      <!-- Every paid booking, card or not, terms step skipped or not. -->
      <p v-if="!waitlist" class="essentials">
        {{ NO_SHOW_LINE }}
        <a v-if="hasCancellationTerms(meet, occurrence)" href="#" class="essentials__link"
           @click.prevent="cancellationTermsOpen = true">Cancellation terms</a>
      </p>
      <crud-error-display :errors="errors" class="mt-3"/>
    </template>

    <!-- ── DONE ── -->
    <template v-else-if="step === 'done'">
      <div class="done">
        <v-icon size="44" color="success" :icon="waitlist ? 'mdi-timer-sand' : 'mdi-check-circle'"/>
        <h3 class="done__title">{{ waitlist ? `You're #${waitlistPosition} on the waitlist` : 'See you there!' }}</h3>
        <p class="done__sub">
          <template v-if="waitlist">
            We'll notify you the moment a spot opens.
            {{ WAITLIST_CHARGE_LINE }}
          </template>
          <template v-else>
            {{ doneChargeLine }}
            <template v-if="freeUntil"> Free cancellation until {{ freeUntil }}.</template>
            <template v-if="bookingWarning"> {{ bookingWarning }}</template>
            {{ NO_SHOW_LINE }}
          </template>
        </p>
      </div>
    </template>

    <template #actions>
      <template v-if="step === 'done'">
        <v-spacer/>
        <v-btn color="primary" variant="flat" @click="close">Done</v-btn>
      </template>
      <template v-else>
        <v-btn v-if="stepIndex > 0" variant="text" @click="back">Back</v-btn>
        <v-btn v-else variant="text" @click="close">Cancel</v-btn>
        <v-spacer/>
        <v-btn
            v-if="step !== 'confirm'"
            color="primary"
            variant="flat"
            :disabled="step === 'terms' && !termsAccepted"
            @click="next"
        >
          Continue
        </v-btn>
        <v-btn
            v-else
            color="primary"
            variant="flat"
            :loading="pending"
            :disabled="cards == null || !hasCard"
            prepend-icon="mdi-check"
            @click="confirm"
        >
          {{ waitlist ? 'Join waitlist' : bookLabel(dateFee) }}
        </v-btn>
      </template>
    </template>
  </tl-dialog>

  <!-- Save-a-card (Stripe Payment Element) — stacked over the flow -->
  <add-card-dialog v-model="addCardOpen" @saved="onCardSaved"/>
  <meet-cancellation-terms-dialog v-model="cancellationTermsOpen" :meet="meet" :occurrence="occurrence"/>
</template>

<style scoped lang="scss">
.payee {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 8px;
}

.rsvp-step {
  font-weight: 500;
  color: rgb(var(--v-theme-textSubtle));
  font-size: 12px;
  margin-left: 6px;
}

/* key/value summary rows (mockup .sum-row) */
.sum-row {
  display: flex;
  gap: 14px;
  justify-content: space-between;
  align-items: baseline;
  padding: 8px 0;
  font-size: 13px;

  & + .sum-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  .k {
    color: rgb(var(--v-theme-textSubtle));
    font-weight: 600;
    font-size: 12px;
    flex-shrink: 0;
  }

  .v {
    font-weight: 600;
    text-align: right;

    &--strong {
      font-weight: 800;
    }

    &--card {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    &__link {
      font-size: 11.5px;
      color: rgb(var(--v-theme-charcoal));
    }
  }
}

/* reassurance strip (mockup .assure) */
.assure {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgb(var(--v-theme-successTint));
  color: rgb(var(--v-theme-charcoal));
  font-size: 11.5px;
  line-height: 1.55;

  .v-icon {
    color: rgb(var(--v-theme-success));
    flex-shrink: 0;
    margin-top: 1px;
  }

  &--warn {
    background: rgb(var(--v-theme-warningTint));

    .v-icon {
      color: rgb(var(--v-theme-warning));
    }
  }
}

.terms-intro {
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
  margin-bottom: 10px;
}

.terms-box {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 12px;
  line-height: 1.65;
  color: rgb(var(--v-theme-charcoal));
  background: rgb(var(--v-theme-light));
}

.essentials {
  margin: 10px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));

  &__link {
    margin-left: 4px;
    font-weight: 600;
    white-space: nowrap;
  }
}

.terms-accept {
  font-size: 13px;
  font-weight: 600;
}

.done {
  text-align: center;
  padding: 8px 0;

  &__title {
    margin: 10px 0 6px;
  }

  &__sub {
    font-size: 12.5px;
    color: rgb(var(--v-theme-textSubtle));
    line-height: 1.6;
  }
}
</style>
